import React, { useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { logActivity } from '../utils/activityLogger';
import '../styles/bug-report-dialog.css';

const BugReportDialog = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [report, setReport] = useState({
    title: '',
    type: 'bug', // bug, suggestion, feature-request
    priority: 'medium', // low, medium, high, critical
    description: '',
    stepsToReproduce: '',
    expectedBehavior: '',
    actualBehavior: '',
    browserInfo: getBrowserInfo(),
    attachments: []
  });
  const fileInputRef = useRef(null);

  function getBrowserInfo() {
    return {
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
      platform: navigator.platform,
      timestamp: new Date().toISOString()
    };
  }

  const handleInputChange = (field, value) => {
    setReport(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploadedFiles = [];
      
      for (const file of files) {
        // Validate file type and size
        if (file.size > 10 * 1024 * 1024) { // 10MB limit
          alert(t('bugReport.fileTooLarge', { filename: file.name }));
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', user.id);
        formData.append('folder', 'bug-reports');

        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${API_BASE}/api/media`, {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          const data = await response.json();
          uploadedFiles.push({
            url: data.url,
            filename: data.filename,
            originalName: file.name,
            size: file.size,
            type: file.type
          });
        } else {
          throw new Error(`Failed to upload ${file.name}`);
        }
      }

      setReport(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));
    } catch (error) {
      console.error('File upload error:', error);
      alert(t('bugReport.uploadFailed'));
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeAttachment = (index) => {
    setReport(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!report.title.trim() || !report.description.trim()) {
      alert(t('bugReport.fillRequired'));
      return;
    }

    logActivity('bug_report_submit', 'issue', null, 
      `User submitted ${report.type}: ${report.title}`,
      { 
        type: report.type, 
        priority: report.priority, 
        titleLength: report.title.length,
        descriptionLength: report.description.length,
        hasAttachments: report.attachments.length > 0,
        attachmentCount: report.attachments.length
      });

    setSubmitting(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/issues`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...report,
          reportedBy: user.id,
          reporterEmail: user.email,
          reporterName: user.name
        }),
      });

      if (response.ok) {
        alert(t('bugReport.submitSuccess'));
        onClose();
        // Reset form
        setReport({
          title: '',
          type: 'bug',
          priority: 'medium',
          description: '',
          stepsToReproduce: '',
          expectedBehavior: '',
          actualBehavior: '',
          browserInfo: getBrowserInfo(),
          attachments: []
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit report');
      }
    } catch (error) {
      console.error('Failed to submit bug report:', error);
      alert(t('bugReport.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div className="bug-report-overlay">
      <div className="bug-report-dialog">
        <div className="bug-report-header">
          <div className="header-content">
            <div className="header-icon">🐛</div>
            <div className="header-text">
              <h2>{t('bugReport.title')}</h2>
              <p>{t('bugReport.subtitle')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="bug-report-content">
          {/* Basic Information */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reportTitle">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,5H21V7H3V5M3,13V11H21V13H3M3,19V17H21V19H3Z" />
                  </svg>
                  {t('bugReport.reportTitle')} *
                </label>
                <input
                  type="text"
                  id="reportTitle"
                  value={report.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={t('bugReport.titlePlaceholder')}
                  disabled={submitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group half">
                <label htmlFor="reportType">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z" />
                  </svg>
                  {t('bugReport.type')}
                </label>
                <select
                  id="reportType"
                  value={report.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  disabled={submitting}
                >
                  <option value="bug">{t('bugReport.typeBug')}</option>
                  <option value="suggestion">{t('bugReport.typeSuggestion')}</option>
                  <option value="feature-request">{t('bugReport.typeFeature')}</option>
                </select>
              </div>

              <div className="form-group half">
                <label htmlFor="reportPriority">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,20A2,2 0 0,1 12,22A2,2 0 0,1 10,20H14M12,2A1,1 0 0,1 13,3V4.08C15.84,4.56 18,7.03 18,10V16L20,18H4L6,16V10C6,7.03 8.16,4.56 11,4.08V3A1,1 0 0,1 12,2Z" />
                  </svg>
                  {t('bugReport.priority')}
                </label>
                <select
                  id="reportPriority"
                  value={report.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  disabled={submitting}
                >
                  <option value="low">{t('bugReport.priorityLow')}</option>
                  <option value="medium">{t('bugReport.priorityMedium')}</option>
                  <option value="high">{t('bugReport.priorityHigh')}</option>
                  <option value="critical">{t('bugReport.priorityCritical')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="form-section">
            <div className="form-group">
              <label>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                {t('bugReport.description')} *
              </label>
              <textarea
                value={report.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('bugReport.descriptionPlaceholder')}
                disabled={submitting}
                rows="6"
              />
            </div>
          </div>

          {/* Bug-specific fields */}
          {report.type === 'bug' && (
            <div className="form-section bug-specific">
              <h3>{t('bugReport.bugDetails')}</h3>
              
              <div className="form-group">
                <label>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z" />
                  </svg>
                  {t('bugReport.stepsToReproduce')}
                </label>
                <textarea
                  value={report.stepsToReproduce}
                  onChange={(e) => handleInputChange('stepsToReproduce', e.target.value)}
                  placeholder={t('bugReport.stepsPlaceholder')}
                  disabled={submitting}
                  rows="4"
                />
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                    </svg>
                    {t('bugReport.expectedBehavior')}
                  </label>
                  <textarea
                    value={report.expectedBehavior}
                    onChange={(e) => handleInputChange('expectedBehavior', e.target.value)}
                    placeholder={t('bugReport.expectedPlaceholder')}
                    disabled={submitting}
                    rows="3"
                  />
                </div>

                <div className="form-group half">
                  <label>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                    </svg>
                    {t('bugReport.actualBehavior')}
                  </label>
                  <textarea
                    value={report.actualBehavior}
                    onChange={(e) => handleInputChange('actualBehavior', e.target.value)}
                    placeholder={t('bugReport.actualPlaceholder')}
                    disabled={submitting}
                    rows="3"
                  />
                </div>
              </div>
            </div>
          )}

          {/* File Attachments */}
          <div className="form-section">
            <div className="form-group">
              <label>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                </svg>
                {t('bugReport.attachments')}
              </label>
              
              <div className="file-upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e.target.files)}
                  multiple
                  accept="image/*,.pdf,.txt,.log"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="upload-button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFiles || submitting}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  {uploadingFiles ? t('bugReport.uploading') : t('bugReport.uploadFiles')}
                </button>
                <p className="upload-help">{t('bugReport.uploadHelp')}</p>
              </div>

              {report.attachments.length > 0 && (
                <div className="attachments-list">
                  {report.attachments.map((attachment, index) => (
                    <div key={index} className="attachment-item">
                      <div className="attachment-info">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                        </svg>
                        <span className="attachment-name">{attachment.originalName}</span>
                        <span className="attachment-size">({formatFileSize(attachment.size)})</span>
                      </div>
                      <button
                        type="button"
                        className="remove-attachment"
                        onClick={() => removeAttachment(index)}
                        disabled={submitting}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="bug-report-actions">
            <button 
              onClick={onClose} 
              className="cancel-button"
              disabled={submitting || uploadingFiles}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
              </svg>
              {t('controls.cancel')}
            </button>
            <button 
              onClick={handleSubmit} 
              className="submit-button"
              disabled={submitting || uploadingFiles || !report.title.trim() || !report.description.trim()}
            >
              {submitting ? (
                <>
                  <div className="button-spinner"></div>
                  {t('bugReport.submitting')}
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z" />
                  </svg>
                  {t('bugReport.submit')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BugReportDialog;